import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MapPin, Clock, Truck } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import { stadtbezirke } from '../mock';

const Einsatzgebiet = () => {
  return (
    <div className="bg-white py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Breadcrumb items={[{ label: 'Einsatzgebiet Dortmund' }]} />
        
        <h1 className="text-4xl font-bold text-[#0F172A] mb-6">Einsatzgebiet Dortmund</h1>
        <p className="text-lg text-gray-700 mb-12 max-w-3xl">
          Wir arbeiten ausschließlich im Stadtgebiet Dortmund – alle 12 Stadtbezirke mit allen Ortsteilen. 
          Kurze Anfahrtswege ermöglichen schnelle Termine und faire Preise ohne hohe Anfahrtskosten.
        </p>

        {/* Map Section */}
        <section className="mb-16">
          <div className="bg-gray-50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Alle 12 Stadtbezirke Dortmunds</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              {stadtbezirke.map((bezirk, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-4 bg-white rounded-lg shadow-sm border border-gray-200"
                >
                  <MapPin className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                  <span className="font-medium text-gray-900">{bezirk}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-700">
              Von der Innenstadt über Hörde, Aplerbeck und Brackel bis zu den äußeren Stadtteilen wie 
              Mengede, Scharnhorst oder Lütgendortmund – wir sind in ganz Dortmund für Sie da.
            </p>
          </div>
        </section>

        {/* Ortsteile */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Ausgewählte Ortsteile (Auswahl)</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[
              'Dortmund-Mitte', 'Dorstfeld', 'Kirchlinde', 'Westfalenhalle',
              'Borsigplatz', 'Hafen', 'Körne', 'Wellinghofen',
              'Berghofen', 'Sölde', 'Westfalenpark', 'Phoenix-See',
              'Asseln', 'Wickede', 'Nette', 'Oespel',
              'Lindenhorst', 'Grevel', 'Kley', 'Barop'
            ].map((ortsteil, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg text-sm text-center"
              >
                {ortsteil}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Dies ist nur eine Auswahl. Wir decken alle Ortsteile innerhalb des Dortmunder Stadtgebiets ab.
          </p>
        </section>

        {/* Special Info */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Besondere Hinweise</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <Truck className="h-10 w-10 text-[#16A34A] mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Anfahrt & Parken</h3>
              <p className="text-gray-700">
                In der Innenstadt und Altbau-Vierteln berücksichtigen wir Parkzonen und Halteverbote. 
                Wir beantragen bei Bedarf Halteverbotsschilder und kalkulieren transparent.
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <MapPin className="h-10 w-10 text-[#16A34A] mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Altbau-Treppenhäuser</h3>
              <p className="text-gray-700">
                Enge Treppenhäuser ohne Aufzug sind kein Problem. Unser Team ist erfahren im Umgang 
                mit schwierigen Zugangswegen und kalkuliert diese ein.
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <Clock className="h-10 w-10 text-[#16A34A] mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Express-Gebiete</h3>
              <p className="text-gray-700">
                Innerhalb Dortmunds können wir oft innerhalb von 1–2 Tagen vor Ort sein. 
                Express-Termine nach Absprache möglich.
              </p>
            </div>
          </div>
        </section>

        {/* Image */}
        <div className="mb-16">
          <img
            src="https://images.unsplash.com/photo-1600518464441-9154a4dea21b"
            alt="Haushaltsauflösung Dortmund - Professioneller Transport"
            className="rounded-lg shadow-lg w-full h-96 object-cover"
          />
        </div>

        {/* Why Local */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Warum nur Dortmund?</h2>
          <div className="bg-gray-50 rounded-lg p-8">
            <p className="text-gray-700 mb-4">
              Als lokaler Dienstleister konzentrieren wir uns bewusst auf Dortmund. Das hat für Sie entscheidende Vorteile:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-[#16A34A] font-bold text-xl flex-shrink-0">✓</span>
                <span className="text-gray-700">
                  <strong>Kurze Anfahrtswege:</strong> Keine hohen Anfahrtskosten, faire Preise
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#16A34A] font-bold text-xl flex-shrink-0">✓</span>
                <span className="text-gray-700">
                  <strong>Schnelle Termine:</strong> Oft innerhalb von 1–3 Tagen vor Ort
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#16A34A] font-bold text-xl flex-shrink-0">✓</span>
                <span className="text-gray-700">
                  <strong>Lokale Expertise:</strong> Wir kennen die Besonderheiten Dortmunds (Altbau, Parkzonen, Stadtteile)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#16A34A] font-bold text-xl flex-shrink-0">✓</span>
                <span className="text-gray-700">
                  <strong>Zuverlässigkeit:</strong> Kurze Wege bedeuten auch kurzfristige Flexibilität
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* CTA */}
        <div className="bg-[#0F172A] text-white rounded-lg p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Ihr Stadtteil ist dabei?</h2>
          <p className="text-lg mb-8 text-gray-300 max-w-2xl mx-auto">
            Perfekt! Fordern Sie jetzt Ihr Festpreis-Angebot an. Wir melden uns schnell.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#16A34A] hover:bg-[#16A34A]/90 text-white">
              <Link to="/kontakt">Jetzt anfragen</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white text-[#0F172A] hover:bg-gray-100">
              <a href="tel:+4923139085116">0231 39085116</a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Einsatzgebiet;